import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatPhone, formatUrl } from "@/lib/format";
import { Save, Building2, Globe, Mail, MapPin, FileText, ShieldCheck, Calendar, ExternalLink, Lock } from "lucide-react";

/**
 * Vendor self-service profile editor. Backed by /api/vendor/me.
 * Vendors can edit description, website, contact info, regions, pricing notes.
 * Identity-level fields (name, category) remain admin-controlled.
 */
export default function VendorProfile() {
  const [me, setMe] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/vendor/me")
      .then((r) => {
        setMe(r.data);
        setForm({
          description: r.data.description || "",
          website: r.data.website || "",
          contact_email: r.data.contact_email || "",
          contact_name: r.data.contact_name || "",
          regions: r.data.regions || "",
          pricing_notes: r.data.pricing_notes || "",
        });
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const r = await api.patch("/vendor/me", form);
      setMe(r.data);
      toast.success("Profile saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-slate-500">Loading profile…</div>;
  if (!me) return <div className="text-sm text-slate-500">No vendor profile linked to this account.</div>;

  return (
    <div className="space-y-5 max-w-4xl" data-testid="vendor-profile">
      <header>
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700">Vendor profile</div>
        <h1 className="font-display text-2xl font-bold text-slate-900 mt-0.5">{me.name}</h1>
        <p className="text-sm text-slate-500 mt-1">
          What clinics see when they discover you in the network. Fields below update your public-facing record.
        </p>
      </header>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <ReadField label="Vendor name" value={me.name} icon={Building2} />
        <ReadField label="Category" value={`${me.category || "—"}${me.subcategory ? ` · ${me.subcategory}` : ""}`} icon={FileText} />
        <ReadField label="Status" value={me.status} icon={Building2} />
        <ReadField label="Compliance attested" value={me.compliance_attested ? "Yes" : "No"} icon={Building2} />
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-700">Public profile</div>

        <Field label="Description">
          <textarea
            data-testid="vendor-profile-description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={4}
            placeholder="What you do, who you serve, what makes you operationally reliable."
            className={textareaCls}
          />
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Website" icon={Globe}>
            <input
              data-testid="vendor-profile-website"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              onBlur={(e) => setForm({ ...form, website: formatUrl(e.target.value) })}
              placeholder="https://yourcompany.com"
              className={inputCls}
            />
          </Field>
          <Field label="Regions served" icon={MapPin}>
            <input
              data-testid="vendor-profile-regions"
              value={form.regions}
              onChange={(e) => setForm({ ...form, regions: e.target.value })}
              placeholder="e.g. CA · NY · TX · Nationwide"
              className={inputCls}
            />
          </Field>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-700">Contact (concierge intros)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Contact name">
            <input
              data-testid="vendor-profile-contact-name"
              value={form.contact_name}
              onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
              className={inputCls}
            />
          </Field>
          <Field label="Contact email" icon={Mail}>
            <input
              data-testid="vendor-profile-contact-email"
              type="email"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              className={inputCls}
            />
          </Field>
        </div>
        <Field label="Pricing notes (internal · admin-visible)">
          <textarea
            data-testid="vendor-profile-pricing-notes"
            value={form.pricing_notes}
            onChange={(e) => setForm({ ...form, pricing_notes: e.target.value })}
            rows={3}
            placeholder="Wholesale ranges, volume tiers, anything our concierge should know."
            className={textareaCls}
          />
        </Field>
      </section>

      <div className="flex items-center justify-end gap-3">
        <button
          type="button"
          data-testid="vendor-profile-save"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-1.5 bg-emerald-600 text-white rounded-full px-5 py-2 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60"
        >
          <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save profile"}
        </button>
      </div>

      <DocumentsOnFile />
    </div>
  );
}

const inputCls = "w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none";
const textareaCls = inputCls;

function Field({ label, icon: Icon, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1 inline-flex items-center gap-1">
        {Icon ? <Icon className="w-3 h-3 text-slate-400" /> : null}
        {label}
      </div>
      {children}
    </label>
  );
}

function ReadField({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1 inline-flex items-center gap-1">
        {Icon ? <Icon className="w-3 h-3 text-slate-400" /> : null}
        {label}
      </div>
      <div className="text-sm font-semibold text-slate-900 capitalize">{value || "—"}</div>
    </div>
  );
}

function DocumentsOnFile() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/documents")
      .then((r) => setDocs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, []);

  const fmtDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  };

  const statusPill = (status) => {
    const map = {
      active: "bg-emerald-50 text-emerald-700 border-emerald-200",
      expired: "bg-rose-50 text-rose-700 border-rose-200",
      pending: "bg-amber-50 text-amber-800 border-amber-200",
    };
    const cls = map[status] || "bg-slate-50 text-slate-700 border-slate-200";
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-[0.12em] border ${cls}`}>
        {status || "—"}
      </span>
    );
  };

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3"
      data-testid="vendor-documents-on-file"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-700 inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
            Documents Nodera has on file
          </div>
          <p className="text-xs text-slate-500 mt-1 max-w-xl">
            Read-only view of your signed agreements, certificates of insurance, and licensure
            attestations as they appear in Nodera's records.
          </p>
        </div>
        <span className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.12em] font-bold text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">
          <Lock className="w-3 h-3" /> Read only
        </span>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : docs.length === 0 ? (
        <div className="text-sm text-slate-600 rounded-xl bg-slate-50 border border-dashed border-slate-200 px-4 py-6 text-center">
          Nodera hasn't recorded any documents for you yet. Contact your Nodera operations
          contact if you believe this is incorrect.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="vendor-documents-list">
          {docs.map((d) => (
            <li
              key={d.id}
              className="py-3 flex items-start gap-3"
              data-testid={`vendor-document-${d.id}`}
            >
              <span className="mt-0.5 w-8 h-8 rounded-lg bg-slate-50 text-slate-600 flex items-center justify-center flex-shrink-0">
                <FileText className="w-3.5 h-3.5" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-slate-900 text-sm">{d.title || "(untitled)"}</span>
                  {d.kind && (
                    <span className="text-[10.5px] uppercase tracking-[0.12em] font-bold text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5 capitalize">
                      {d.kind}
                    </span>
                  )}
                  {statusPill(d.status)}
                </div>
                <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5 items-center">
                  {d.expires_at && (
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> Expires {fmtDate(d.expires_at)}
                    </span>
                  )}
                  {d.created_at && <span>Added {fmtDate(d.created_at)}</span>}
                </div>
                {d.notes && <div className="text-xs text-slate-600 mt-1 leading-relaxed">{d.notes}</div>}
              </div>
              {d.url && (
                <a
                  href={d.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`vendor-document-link-${d.id}`}
                  className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-emerald-700 hover:text-emerald-900 whitespace-nowrap"
                >
                  Open <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

